// Static Moat: a wall of up to three orthogonally connected squares that
// blocks movement onto or through it, for everybody, permanently (§26 C13,
// §31 C10).

import { fileOf, rankOf } from "../board";
import { hasCell, invalidateMoves, moatLeft, pieceAt, type Position } from "../position";
import { RULES } from "../rules";

/**
 * The expiry a permanent wall carries (§31 C10): the `moat` map stays an
 * expiry map, so `moatLeft`, the hash and `orphans.test.ts` are unchanged.
 */
export const MOAT_FOREVER = 0x7fffffff;

/** Why a moat cannot be drawn there, or null. */
export type MoatRefusal =
  | "not-a-square"
  | "square-occupied"
  | "already-moated"
  | "not-a-chain"
  | "moat-too-wide";

/** Orthogonally touching, `bridges.ts`'s reading: a packed offset wraps at the file band. */
const orthogonal = (a: number, b: number): boolean =>
  Math.abs(fileOf(a) - fileOf(b)) + Math.abs(rankOf(a) - rankOf(b)) === 1;

/** Every rule about where a wall may be drawn, so the reducer and the board agree (§26 C13). */
export function refuseMoat(position: Position, squares: readonly number[]): MoatRefusal | null {
  if (squares.length > RULES.moatSquares) return "moat-too-wide";

  for (let i = 0; i < squares.length; i++) {
    const at = squares[i];
    if (squares.indexOf(at) !== i) return "not-a-chain";
    if (!hasCell(position, at)) return "not-a-square";
    if (pieceAt(position, at) !== 0) return "square-occupied";
    if (moatLeft(position, at) > 0) return "already-moated";
    if (i > 0 && !orthogonal(squares[i - 1], at)) return "not-a-chain";
  }
  return null;
}

/** Wall these squares for good. The one `moat` write outside `position.ts`. */
export function placeMoat(position: Position, squares: readonly number[]): void {
  invalidateMoves(position);
  for (const sq of squares) position.moat.set(sq, MOAT_FOREVER);
}
